// M5.2 — quest-giver modal. Same chassis as vendor-window: header, tabs,
// scrollable list per tab, footer. Three tabs: Available / Active /
// Completable. Each row carries an Accept / Abandon / Turn-in button
// matching its status. Cart-style: actions fire individual server messages
// (no batching), because quest commits are atomic per quest.

import {
  getItemTemplate,
  getMobTemplate,
  getQuestTemplate,
  type QuestTemplate,
  type QuestObjective,
} from "@vinyl/shared";
import { el } from "./dom.js";
import { setupWindow, type WindowChromeHandle } from "./window-chrome.js";

export interface QuestAvailable {
  questId: string;
}
export interface QuestActive {
  questId: string;
  progress: number[];
}
export interface QuestCompletable {
  questId: string;
  progress: number[];
}

export interface QuestWindowCallbacks {
  onAccept: (npcId: string, questId: string) => void;
  onAbandon: (questId: string) => void;
  onTurnIn: (npcId: string, questId: string) => void;
  onClose: () => void;
}

type Tab = "available" | "active" | "completable";

export class QuestWindow {
  readonly root: HTMLElement;
  private npcId: string | null = null;
  private vendorName = "Quest Giver";
  private dialogText = "";
  private available: QuestAvailable[] = [];
  private active: QuestActive[] = [];
  private completable: QuestCompletable[] = [];
  private activeTab: Tab = "available";
  private chrome!: WindowChromeHandle;

  private setQuestTitle!: (text: string) => void;
  private dialogEl: HTMLElement;
  private listEl: HTMLElement;
  private availTabBtn: HTMLButtonElement;
  private activeTabBtn: HTMLButtonElement;
  private completableTabBtn: HTMLButtonElement;

  constructor(private readonly cb: QuestWindowCallbacks) {
    this.dialogEl = el("p", { class: "quest-dialog" });
    this.listEl = el("div", { class: "quest-list" });

    this.availTabBtn = el(
      "button",
      { class: "quest-tab", type: "button" },
      "Available",
    ) as HTMLButtonElement;
    this.activeTabBtn = el(
      "button",
      { class: "quest-tab", type: "button" },
      "Active",
    ) as HTMLButtonElement;
    this.completableTabBtn = el(
      "button",
      { class: "quest-tab", type: "button" },
      "Ready to turn in",
    ) as HTMLButtonElement;
    this.availTabBtn.addEventListener("click", () => this.setTab("available"));
    this.activeTabBtn.addEventListener("click", () => this.setTab("active"));
    this.completableTabBtn.addEventListener("click", () => this.setTab("completable"));

    const cancelBtn = el(
      "button",
      { class: "btn-ghost", type: "button" },
      "Close",
    );
    cancelBtn.addEventListener("click", () => this.close());

    this.root = el("aside", { class: "quest-window hidden" });
    this.chrome = setupWindow({
      windowEl: this.root,
      title: "Quest Giver",
      storageKey: "quest",
      onClose: () => this.onClosed(),
    });
    this.setQuestTitle = this.chrome.setTitle;
    this.root.append(
      this.chrome.header,
      this.dialogEl,
      el(
        "div",
        { class: "quest-tabs" },
        this.availTabBtn,
        this.activeTabBtn,
        this.completableTabBtn,
      ),
      this.listEl,
      el("div", { class: "quest-footer" }, cancelBtn),
    );
  }

  open(args: {
    npcId: string;
    vendorName: string;
    dialogText: string;
    available: QuestAvailable[];
    active: QuestActive[];
    completable: QuestCompletable[];
  }): void {
    this.npcId = args.npcId;
    this.vendorName = args.vendorName;
    this.dialogText = args.dialogText;
    this.available = args.available;
    this.active = args.active;
    this.completable = args.completable;
    this.chrome.open();
    this.setQuestTitle(this.vendorName);
    this.dialogEl.textContent = this.dialogText;
    // Default to Completable if there's anything to turn in, then Available,
    // then Active. Saves a click when the player just finished a quest.
    if (this.completable.length > 0) this.activeTab = "completable";
    else if (this.available.length > 0) this.activeTab = "available";
    else this.activeTab = "active";
    this.renderAll();
  }

  // Called when the quest log mutates while the modal is open (server sends
  // questLogUpdate). The full log lets us recompute the three columns by
  // intersecting with the originally-shown questIds. We trust the open()
  // payload's questIds; if anything dropped (e.g. the player abandoned via
  // another path), it just disappears from the list.
  syncFromLog(
    entries: { questId: string; status: string; progress: number[] }[],
  ): void {
    if (!this.chrome.isOpen()) return;
    // Only adjust rows whose questId is already known to this open() session.
    const known = new Set<string>([
      ...this.available.map((q) => q.questId),
      ...this.active.map((q) => q.questId),
      ...this.completable.map((q) => q.questId),
    ]);
    const nextAvail: QuestAvailable[] = [];
    const nextActive: QuestActive[] = [];
    const nextCompletable: QuestCompletable[] = [];
    for (const qid of known) {
      const entry = entries.find((e) => e.questId === qid);
      if (!entry) {
        nextAvail.push({ questId: qid });
        continue;
      }
      if (entry.status === "active") {
        nextActive.push({ questId: qid, progress: entry.progress });
      } else if (entry.status === "completable") {
        nextCompletable.push({ questId: qid, progress: entry.progress });
      }
      // completed: drop from the dialog entirely (one-shot).
    }
    this.available = nextAvail;
    this.active = nextActive;
    this.completable = nextCompletable;
    this.renderAll();
  }

  isVisible(): boolean {
    return this.chrome.isOpen();
  }

  forceClose(): void {
    this.close();
  }

  close(): void { this.chrome.close(); }

  private onClosed(): void {
    this.npcId = null;
    this.cb.onClose();
  }

  private setTab(tab: Tab): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.renderAll();
  }

  private renderAll(): void {
    this.availTabBtn.classList.toggle("quest-tab-active", this.activeTab === "available");
    this.activeTabBtn.classList.toggle("quest-tab-active", this.activeTab === "active");
    this.completableTabBtn.classList.toggle("quest-tab-active", this.activeTab === "completable");
    this.availTabBtn.textContent =
      this.available.length > 0 ? `Available · ${this.available.length}` : "Available";
    this.activeTabBtn.textContent =
      this.active.length > 0 ? `Active · ${this.active.length}` : "Active";
    this.completableTabBtn.textContent =
      this.completable.length > 0
        ? `Ready · ${this.completable.length}`
        : "Ready to turn in";
    this.listEl.replaceChildren();
    if (this.activeTab === "available") this.renderAvailable();
    else if (this.activeTab === "active") this.renderActive();
    else this.renderCompletable();
  }

  private renderAvailable(): void {
    if (this.available.length === 0) {
      this.listEl.append(el("div", { class: "quest-empty" }, "Nothing new on the board."));
      return;
    }
    for (const q of this.available) {
      const tpl = getQuestTemplate(q.questId);
      if (!tpl) continue;
      this.listEl.append(
        this.makeRow(tpl, this.objectivesText(tpl, tpl.objectives.map(() => 0)), {
          label: "Accept",
          onClick: () => {
            if (!this.npcId) return;
            const npcId = this.npcId;
            // Close the modal so the NPC's onAccept speech bubble (broadcast
            // from the server) is visible. Player re-clicks the NPC to accept
            // more.
            this.close();
            this.cb.onAccept(npcId, q.questId);
          },
        }),
      );
    }
  }

  private renderActive(): void {
    if (this.active.length === 0) {
      this.listEl.append(el("div", { class: "quest-empty" }, "You're not on any of these quests."));
      return;
    }
    for (const q of this.active) {
      const tpl = getQuestTemplate(q.questId);
      if (!tpl) continue;
      this.listEl.append(
        this.makeRow(tpl, this.objectivesText(tpl, q.progress), {
          label: "Abandon",
          variant: "danger",
          onClick: () => {
            const qid = q.questId;
            this.close();
            this.cb.onAbandon(qid);
          },
        }),
      );
    }
  }

  private renderCompletable(): void {
    if (this.completable.length === 0) {
      this.listEl.append(el("div", { class: "quest-empty" }, "Nothing ready to turn in."));
      return;
    }
    for (const q of this.completable) {
      const tpl = getQuestTemplate(q.questId);
      if (!tpl) continue;
      this.listEl.append(
        this.makeRow(tpl, this.objectivesText(tpl, q.progress), {
          label: "Turn in",
          variant: "primary",
          onClick: () => {
            if (!this.npcId) return;
            const npcId = this.npcId;
            // Close the modal so the NPC's onTurnIn speech bubble (broadcast
            // from the server) is visible behind it.
            this.close();
            this.cb.onTurnIn(npcId, q.questId);
          },
        }),
      );
    }
  }

  private makeRow(
    tpl: QuestTemplate,
    objectivesLine: string,
    action: { label: string; variant?: "primary" | "danger"; onClick: () => void },
  ): HTMLElement {
    const name = el("div", { class: "quest-row-name" }, tpl.name);
    const desc = el("div", { class: "quest-row-desc" }, tpl.description);
    const objectives = el("div", { class: "quest-row-objectives" }, objectivesLine);
    const rewards = el("div", { class: "quest-row-rewards" }, this.rewardsText(tpl));
    const btnClass =
      action.variant === "primary" ? "btn" : action.variant === "danger" ? "btn-danger" : "btn-ghost";
    const btn = el(
      "button",
      { class: `${btnClass} quest-row-btn`, type: "button" },
      action.label,
    );
    btn.addEventListener("click", action.onClick);
    return el(
      "div",
      { class: "quest-row" },
      el("div", { class: "quest-row-main" }, name, desc, objectives, rewards),
      btn,
    );
  }

  private objectivesText(tpl: QuestTemplate, progress: number[]): string {
    const parts: string[] = [];
    for (let i = 0; i < tpl.objectives.length; i++) {
      const obj = tpl.objectives[i]!;
      const have = progress[i] ?? 0;
      parts.push(this.objectiveLine(obj, have));
    }
    return parts.join(" · ");
  }

  private objectiveLine(obj: QuestObjective, have: number): string {
    if (obj.kind === "kill") {
      const mobTpl = getMobTemplate(obj.mobTemplateId);
      const mobName = mobTpl ? mobTpl.name : obj.mobTemplateId;
      const plural = obj.count > 1 ? "s" : "";
      return `${have} / ${obj.count} ${mobName}${plural}`;
    }
    return "";
  }

  private rewardsText(tpl: QuestTemplate): string {
    const parts: string[] = [];
    for (const r of tpl.rewards) {
      if (r.kind === "copper") parts.push(`${r.amount} copper`);
      else if (r.kind === "xp") parts.push(`${r.amount} XP`);
      else if (r.kind === "item") {
        // Look up the template for a human-readable name; fall back to the
        // raw id if the catalogue hasn't caught up yet.
        const tplName = getItemTemplate(r.templateId)?.name ?? r.templateId;
        parts.push(r.qty > 1 ? `${r.qty}× ${tplName}` : tplName);
      }
    }
    return parts.length > 0 ? `Reward: ${parts.join(", ")}` : "";
  }
}
